import { useState, useCallback, useEffect, useRef } from 'react';

import useBasename from '../../../routing/useBasename';
import { BASENAME } from '../../../../constants/basenames';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.5;

const usePhotoZoom = () => {
  const basename = useBasename();
  const enabled = basename === BASENAME.PRO;
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [containerMounted, setContainerMounted] = useState(false);
  const lastPanPosition = useRef(null);
  const lastTouchDistance = useRef(null);
  const containerRef = useRef(null);

  const setContainerRef = useCallback(node => {
    containerRef.current = node;
    setContainerMounted(!!node);
  }, []);

  const resetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    lastPanPosition.current = null;
    lastTouchDistance.current = null;
  }, []);

  const zoomToPoint = useCallback((newScale, clientX, clientY) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const offsetX = mouseX - centerX;
    const offsetY = mouseY - centerY;

    setScale(prevScale => {
      const scaleRatio = newScale / prevScale;
      setPosition(prev => ({
        x: prev.x - offsetX * (scaleRatio - 1),
        y: prev.y - offsetY * (scaleRatio - 1),
      }));
      return newScale;
    });
  }, []);

  const zoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + ZOOM_STEP, MAX_SCALE));
  }, []);

  const zoomOut = useCallback(() => {
    setScale(prev => {
      const newScale = Math.max(prev - ZOOM_STEP, MIN_SCALE);
      if (newScale === MIN_SCALE) {
        setPosition({ x: 0, y: 0 });
      }
      return newScale;
    });
  }, []);
  const handleWheel = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();

    setScale(currentScale => {
      const zoomDirection = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      const newScale = Math.max(
        MIN_SCALE,
        Math.min(MAX_SCALE, currentScale + zoomDirection),
      );

      if (newScale === MIN_SCALE) {
        setPosition({ x: 0, y: 0 });
        return MIN_SCALE;
      }

      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const offsetX = mouseX - centerX;
        const offsetY = mouseY - centerY;
        const scaleRatio = newScale / currentScale;

        setPosition(prev => ({
          x: prev.x - offsetX * (scaleRatio - 1),
          y: prev.y - offsetY * (scaleRatio - 1),
        }));
      }

      return newScale;
    });
  }, []);

  const handleDoubleClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      scale > 1 ? resetZoom() : zoomToPoint(2, e.clientX, e.clientY);
    },
    [scale, resetZoom, zoomToPoint],
  );

  const handleMouseDown = useCallback(
    e => {
      if (scale > 1) {
        e.preventDefault();
        e.stopPropagation();
        setIsPanning(true);
        lastPanPosition.current = { x: e.clientX, y: e.clientY };
      }
    },
    [scale],
  );

  const handleMouseMove = useCallback(
    e => {
      if (isPanning && lastPanPosition.current && scale > 1) {
        e.preventDefault();
        e.stopPropagation();
        const deltaX = e.clientX - lastPanPosition.current.x;
        const deltaY = e.clientY - lastPanPosition.current.y;
        setPosition(prev => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY,
        }));
        lastPanPosition.current = { x: e.clientX, y: e.clientY };
      }
    },
    [isPanning, scale],
  );

  const handleMouseUp = useCallback(
    e => {
      if (isPanning) {
        e.preventDefault();
        e.stopPropagation();
      }
      setIsPanning(false);
      lastPanPosition.current = null;
    },
    [isPanning],
  );

  const handleTouchStart = useCallback(
    e => {
      if (e.touches.length === 2) {
        e.preventDefault();
        e.stopPropagation();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY,
        );
        lastTouchDistance.current = distance;
      } else if (e.touches.length === 1 && scale > 1) {
        e.preventDefault();
        e.stopPropagation();
        setIsPanning(true);
        lastPanPosition.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };
      }
    },
    [scale],
  );

  const handleTouchMove = useCallback(
    e => {
      if (e.touches.length === 2 && lastTouchDistance.current) {
        e.preventDefault();
        e.stopPropagation();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY,
        );
        const delta = distance - lastTouchDistance.current;
        const newScale = Math.max(
          MIN_SCALE,
          Math.min(MAX_SCALE, scale + delta / 100),
        );
        setScale(newScale);
        lastTouchDistance.current = distance;
      } else if (
        e.touches.length === 1 &&
        isPanning &&
        lastPanPosition.current
      ) {
        e.preventDefault();
        e.stopPropagation();
        const deltaX = e.touches[0].clientX - lastPanPosition.current.x;
        const deltaY = e.touches[0].clientY - lastPanPosition.current.y;
        setPosition(prev => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY,
        }));
        lastPanPosition.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };
      }
    },
    [scale, isPanning],
  );

  const handleTouchEnd = useCallback(
    e => {
      if (lastTouchDistance.current || isPanning) {
        e.preventDefault();
        e.stopPropagation();
      }
      lastTouchDistance.current = null;
      setIsPanning(false);
      lastPanPosition.current = null;
    },
    [isPanning],
  );

  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('dblclick', handleDoubleClick);
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('dblclick', handleDoubleClick);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [
    enabled,
    handleWheel,
    handleDoubleClick,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    containerMounted,
  ]);

  useEffect(() => {
    if (!enabled) return;

    if (isPanning) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [enabled, isPanning, handleMouseMove, handleMouseUp]);

  const transformStyle = {
    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
    transition: isPanning ? 'none' : 'transform 0.2s ease-out',
    cursor: scale > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  };

  return {
    scale,
    position,
    resetZoom,
    zoomIn,
    zoomOut,
    transformStyle,
    containerRef: setContainerRef,
    isZoomed: scale > 1,
    enabled,
  };
};

export default usePhotoZoom;
